import React, { useState, useEffect } from 'react';
import { usePersona, type AudiencePersona } from '@/contexts/persona-context';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Target, Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import PersonaForm from './persona-form';

interface QuickPersonaCreatorProps {
  initialData?: AudiencePersona;
  preSelectedBrandId?: number;
  mode?: 'create' | 'edit' | 'manage';
  onClose: () => void;
  onPersonaCreated: (persona: AudiencePersona) => void;
}

export default function QuickPersonaCreator({
  initialData,
  preSelectedBrandId,
  mode,
  onClose,
  onPersonaCreated,
}: QuickPersonaCreatorProps) {
  const { savedPersonas, deletePersona } = usePersona();
  const [isLoading, setIsLoading] = useState(false);

  // Determine effective mode from explicit prop or initialData presence
  const effectiveMode = mode || (initialData ? 'edit' : 'create');

  // In manage mode, track which persona is selected for editing
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | undefined>(
    initialData?.id || (effectiveMode === 'manage' && savedPersonas.length > 0 ? savedPersonas[0].id : undefined)
  );

  // Get the persona data to pass to the form
  const selectedPersona = effectiveMode === 'manage'
    ? savedPersonas.find(p => p.id === selectedPersonaId)
    : initialData;

  // Fetch brands for the user (for manage mode persona selector)
  const { data: brands = [] } = useQuery({
    queryKey: ['/api/brands'],
    queryFn: async () => {
      const response = await fetch('/api/brands', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) return [];
      return response.json();
    },
  });

  const handleDelete = async () => {
    if (!selectedPersonaId) return;
    const personaName = savedPersonas.find(p => p.id === selectedPersonaId)?.profileName || 'this persona';
    if (!window.confirm(`Are you sure you want to delete "${personaName}"? This cannot be undone.`)) return;

    setIsLoading(true);
    try {
      await deletePersona(selectedPersonaId);
      // Select next available persona or close if none remain
      const remaining = savedPersonas.filter(p => p.id !== selectedPersonaId);
      if (remaining.length > 0) {
        setSelectedPersonaId(remaining[0].id);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Failed to delete persona:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDialogTitle = () => {
    switch (effectiveMode) {
      case 'manage':
        return 'Manage Personas';
      case 'edit':
        return 'Edit Persona';
      default:
        return 'Create Persona';
    }
  };

  const getDialogDescription = () => {
    switch (effectiveMode) {
      case 'manage':
        return 'Select a persona to edit or delete';
      default:
        return 'Define your target audience characteristics to filter and match opportunities';
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 pb-6">
          {/* Persona Selector (manage mode only) */}
          {effectiveMode === 'manage' && savedPersonas.length > 0 && (
            <div className="mb-4">
              <Label className="mb-2 block">Select Persona</Label>
              <div className="flex gap-2">
                <Select
                  value={selectedPersonaId?.toString() || ''}
                  onValueChange={(value) => setSelectedPersonaId(parseInt(value))}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Choose a persona to edit" />
                  </SelectTrigger>
                  <SelectContent>
                    {savedPersonas.map((persona) => (
                      <SelectItem key={persona.id} value={persona.id!.toString()}>
                        <div className="flex items-center gap-2">
                          <span>{(persona as any).icon || '🎯'}</span>
                          <span>{persona.profileName}</span>
                          {persona.brandId && brands.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({brands.find((b: any) => b.id === persona.brandId)?.name || ''})
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPersonaId && (
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={handleDelete}
                    disabled={isLoading}
                    title="Delete persona"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          <PersonaForm
            key={effectiveMode === 'manage' ? selectedPersonaId : initialData?.id}
            initialData={selectedPersona}
            preSelectedBrandId={preSelectedBrandId}
            onSave={(persona) => {
              onPersonaCreated(persona);
              onClose();
            }}
            onCancel={onClose}
            showCancelButton
            showDeleteButton={effectiveMode === 'edit' && !!initialData?.id}
            onDelete={effectiveMode === 'edit' && initialData?.id ? async () => {
              if (!window.confirm(`Are you sure you want to delete "${initialData.profileName}"? This cannot be undone.`)) return;
              await deletePersona(initialData.id);
              onClose();
            } : undefined}
            isLoading={isLoading}
            saveButtonText={
              effectiveMode === 'manage'
                ? 'Update Persona'
                : effectiveMode === 'edit'
                ? 'Update Persona'
                : 'Create & Apply Persona'
            }
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
